import { defineComponent as _defineComponent } from 'vue'
import { PROVIDE_KEY_SHOW_TOAST_MESSAGE, ShowToastMessage } from "@/types";
import {
  computed,
  inject,
  onMounted,
  onBeforeUnmount,
  PropType,
  ref,
  watch,
  Ref,
} from "vue";
import {
  IBusLocation,
  ILineInfoData,
  IPath,
  ITurn,
} from "@/domain/entities/lineInfo";
import { IBusStopData } from "@/domain/entities/busStop";
import NavigationBar from "../shared/NavigationBar.vue";
import { useServiceStore } from "@/stores/serviceStore";
import MapZoomLevelSetter from "./MapZoomLevelSetter.vue";

import InlineSvg from "vue-inline-svg";
import LineInfoDetailHeader from "../line/LineInfoDetailHeader.vue";
import { useZoomLevel } from "@/composables/map/useZoomLevel";
import { useCreateBusStopMarkers } from "@/composables/map/useCreateBusStopMarkers";
import { useBusLocationMarkers } from "@/composables/map/useBusLocationMarkers";
import { useNaverMapPaths } from "@/composables/map/useNaverMapPaths";
import RoadEventListModal from "../line/roadEvent/RoadEventListModal.vue";
import _ from "lodash";
import PageModal from "../shared/modal/base/PageModal.vue";
import { useCurrentLocationMarker } from "@/composables/map/useCurrentLocationMarker";
import {
  GetCurrentLocation,
  Location,
  PROVIDE_KEY_CURRENT_LOCATION,
  PROVIDE_KEY_GET_CURRENT_LOCATION,
  PROVIDE_KEY_IS_LOCATION_PERMISSION_DENIED,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  ShowErrorModal,
} from "@/types";


const __default__ = {
  name: "LineTurnPathMap",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  paths: {
    type: Array as PropType<IPath[]>,
    default: () => [],
  },
  detourPaths: {
    type: Array as PropType<IPath[]>,
    default: () => [
      // { latitude: 37.2498078, longitude: 127.4759946 },
      // { latitude: 37.24982, longitude: 127.4761 },
      // { latitude: 37.24988, longitude: 127.47605 },
      // { latitude: 37.25, longitude: 127.4759 },
      // { latitude: 37.24995, longitude: 127.4752 },
      // { latitude: 37.25005, longitude: 127.4753 },
      // { latitude: 37.25015, longitude: 127.4755 },
      // { latitude: 37.2509, longitude: 127.4757 },
    ],
  },
  detourReason: {
    type: String,
  },
  detourBeginDtm: {
    type: String,
  },
  detourEndDtm: {
    type: String,
  },
  busStops: {
    type: Array as PropType<Array<IBusStopData>> | null,
    defualt: null,
  },
  busLocations: {
    type: Array as PropType<IBusLocation[]>,
    default: () => [],
  },
  lineInfo: {
    type: Object as PropType<ILineInfoData>,
    required: true,
  },
  isLinePathExisted: {
    type: Boolean,
    default: false,
  },
  isFavoriteLine: {
    type: Boolean,
    default: false,
  },
  shouldShowBusLocations: {
    type: Boolean,
    default: true,
  },
  isBookable: {
    type: Boolean,
    default: false,
  },
  isInsideShuttle: {
    type: Boolean,
    default: false,
  },
  showCurrentLocation: {
    type: Boolean,
    default: false,
  },
  selectedTurn: {
    type: Object as PropType<ITurn>,
  },
  /** 모든 정류장에 항상 이름을 표시할지 여부 */
  showAllBusStopNames: {
    type: Boolean,
    default: false,
  },
},
  emits: ["onClickRefreshBtn", "onClickClose", "onClickDetailBtn", "onClickFavoriteBtn"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props;

const emit = __emit;

const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const serviceStore = useServiceStore();
const shouldShowTurnPath = serviceStore.serviceInfo?.shouldShowTurnPath;

const title = computed(() => {
  if (!shouldShowTurnPath) {
    return "지도보기";
  }
  return props.paths?.length ? "경로보기" : "지도보기";
});

const emitOnClickClose = () => {
  emit("onClickClose");
};

const emitOnClickDetailBtn = () => {
  emit("onClickDetailBtn");
};

const emitOnClickFavoriteBtn = () => {
  emit("onClickFavoriteBtn", !props.isFavoriteLine);
};

const refreshButton = ref();
const isRotating = ref(false);

const emitOnClickRefreshBtn = () => {
  if (!props.shouldShowBusLocations || isRotating.value) return;
  isRotating.value = true;

  refreshButton.value?.classList.add("rotate");
  setTimeout(() => {
    refreshButton.value?.classList.remove("rotate");
    isRotating.value = false;
  }, 500);

  emit("onClickRefreshBtn");
};

const mapContainer = ref<HTMLElement>();

let map: any = undefined;

const addScript = () => {
  const naverMapScript = document.createElement("script");
  naverMapScript.setAttribute(
    "src",
    `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${process.env.VUE_APP_NAVER_CLIENT_ID}&submodules=panorama,drawing`
  );
  naverMapScript.async = true;
  document.head.appendChild(naverMapScript);
  naverMapScript.onload = () => initMap();
};

const getCenterOfPaths = (paths: IPath[]): IPath => {
  if (!paths.length) {
    return {
      latitude: props.busStops?.[0]?.latitude ?? 37.4044893,
      longitude: props.busStops?.[0]?.longitude ?? 127.0982259,
    };
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  paths.forEach((path) => {
    if (path.latitude < minLat) minLat = path.latitude;
    if (path.latitude > maxLat) maxLat = path.latitude;
    if (path.longitude < minLng) minLng = path.longitude;
    if (path.longitude > maxLng) maxLng = path.longitude;
  });

  return {
    latitude: (minLat + maxLat) / 2,
    longitude: (minLng + maxLng) / 2,
  };
};

const drawDetourPaths = (map: any) => {
  if (!props.detourPaths.length) return;

  const addedPaths = _.differenceWith(
    props.detourPaths,
    props.paths,
    _.isEqual
  ) as IPath[];

  if (!addedPaths.length) return;

  drawNaverMapPaths(map, {
    paths: addedPaths,
    strokeColor: "#ff0000",
  });
};

const initMap = () => {
  const center = getCenterOfPaths(props.paths);

  map = new window.naver.maps.Map("map", {
    center: new window.naver.maps.LatLng(center.latitude, center.longitude),
    zoom: zoomLevel.value,
    mapTypes: new window.naver.maps.MapTypeRegistry({
      normal: new window.naver.maps.NaverStyleMapTypeOptions.getNormalMap({
        overlayType: "bg.ol.ts.lko",
      }),
    }),
    minZoom: MIN_ZOOM_LEVEL,
    maxZoom: MAX_ZOOM_LEVEL,
  });

  createBusStopMarkers(map, props.busStops, undefined, handleBusStopClick);

  // 초기 상태에서 showAllBusStopNames가 true라면 모든 정류장 이름을 infoWindow 스타일로 표시
  if (props.showAllBusStopNames) {
    updateSelectedBusStopMarkerWithInfoWindow(undefined, true);
  }

  if (props.shouldShowBusLocations) {
    createBusLocationMarkers(map, props.busLocations);
  }

  const hasDetourPath = props.detourPaths.length;
  drawNaverMapPaths(map, {
    paths: props.paths,
    strokeColor: hasDetourPath ? "#B3B3B3" : "#8dcd33b3",
  });

  if (hasDetourPath) {
    drawDetourPaths(map);
  }

  addZoomChangedNaverEventListener(map);
};

/** 지도 Zoom Level */
const MIN_ZOOM_LEVEL = 8;
const MAX_ZOOM_LEVEL = 18;

const {
  zoomLevel,
  increaseZoomLevel,
  decreaseZoomLevel,
  addZoomChangedNaverEventListener,
  removeZoomChangedNaverEventListener,
} = useZoomLevel({
  minZoom: MIN_ZOOM_LEVEL,
  maxZoom: MAX_ZOOM_LEVEL,
  initialZoomLevel: props.isInsideShuttle ? 15 : 10,
});

/** 버스 정류장 마커 생성 */
const {
  createBusStopMarkers,
  getBusStopIconContents,
  updateSelectedBusStopMarkerWithInfoWindow,
} = useCreateBusStopMarkers();

/** 선택된 정류장 상태 관리 */
const selectedBusStopId = ref<string | undefined>(undefined);

const handleBusStopClick = (busStop: IBusStopData) => {
  // 같은 정류장을 다시 클릭한 경우 선택 해제
  if (selectedBusStopId.value === busStop.id) {
    selectedBusStopId.value = undefined;
  } else {
    selectedBusStopId.value = busStop.id;
  }

  // 마커 업데이트
  updateSelectedBusStopMarkerWithInfoWindow(
    selectedBusStopId.value,
    props.showAllBusStopNames
  );
};

/** 버스 실시간 위치 마커 생성 */
const { createBusLocationMarkers, removeBusLocationMarkers } =
  useBusLocationMarkers();

const { drawNaverMapPaths } = useNaverMapPaths();

const handleShowToastMessage = () => {
  if (shouldShowTurnPath && !props.paths.length) {
    showToastMessage("노선 경로를 찾을 수 없습니다");
    return;
  }

  if (props.shouldShowBusLocations && !props.busLocations.length) {
    showToastMessage("현재 운행중인 버스 정보를 찾을 수 없습니다");
    return;
  }
};

// 교통정보 모달
const shouldShowRoadEventListModal = ref(false);
const toggleShowRoadEventListModal = (show: boolean) => {
  shouldShowRoadEventListModal.value = show;
};

onMounted(() => {
  window.naver ? initMap() : addScript();
  handleShowToastMessage();
});

onBeforeUnmount(() => {
  removeZoomChangedNaverEventListener();
});

const isSameBusLocation = (
  newLocations: IBusLocation[],
  oldLocations: IBusLocation[]
) => {
  if (!oldLocations || newLocations.length !== oldLocations.length)
    return false;

  return newLocations.every((newLocation, index) => {
    const targetOldLocation = oldLocations[index];
    return (
      newLocation.lat === targetOldLocation.lat &&
      newLocation.lng === targetOldLocation.lng
    );
  });
};

watch(
  () => props.busLocations,
  (newBusLocations, oldBusLocations) => {
    if (!props.shouldShowBusLocations) return;
    if (isSameBusLocation(newBusLocations, oldBusLocations)) return;
    removeBusLocationMarkers();
    createBusLocationMarkers(map, newBusLocations);
  }
);

// showAllBusStopNames prop 변경 시 마커 업데이트
// watch(
//   () => props.showAllBusStopNames,
//   (newValue) => {
//     // showAllBusStopNames 변경 시 항상 모든 마커를 업데이트
//     updateSelectedBusStopMarkerWithInfoWindow(
//       selectedBusStopId.value,
//       newValue
//     );
//   }
// );

// 위치 관련 inject
const getCurrentLocation = inject(
  PROVIDE_KEY_GET_CURRENT_LOCATION
) as GetCurrentLocation;
const currentLocation = inject(PROVIDE_KEY_CURRENT_LOCATION) as Ref<
  Location | undefined
>;
const isLocationPermissionDenied = inject(
  PROVIDE_KEY_IS_LOCATION_PERMISSION_DENIED
) as Ref<boolean>;
const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;

const currentLat = ref<number | undefined>(undefined);
const currentLng = ref<number | undefined>(undefined);

/** 마커 생성 */
const createMarker = (
  lat: number,
  lng: number,
  markerImage: () => any,
  zIndex: number
) => {
  const position = new window.naver.maps.LatLng(lat, lng);
  const markerOptions = {
    position: position,
    map: map,
    icon: markerImage(),
    zIndex: zIndex,
  };
  return new window.naver.maps.Marker(markerOptions);
};

// 현재 위치 마커 composable
const { createCurrentLocationMarker, removeCurrentLocationMarker } =
  useCurrentLocationMarker(createMarker);

const moveMapToCurrentLocation = async () => {
  if (!props.showCurrentLocation) return;
  if (!getCurrentLocation) return;

  try {
    await getCurrentLocation();
    if (
      currentLocation?.value &&
      currentLocation.value.latitude &&
      currentLocation.value.longitude
    ) {
      currentLat.value = currentLocation.value.latitude;
      currentLng.value = currentLocation.value.longitude;
      if (map) {
        map.setCenter(
          new window.naver.maps.LatLng(currentLat.value, currentLng.value)
        );
        // 마커 표시
        createCurrentLocationMarker(map, currentLat.value, currentLng.value);
      }
    } else {
      if (showErrorModal) {
        showErrorModal(
          "현재 위치 확인 불가",
          "현재 위치를 확인할 수 없습니다. 위치 권한이 허용되었는지 확인해주세요."
        );
      }
    }
  } catch (e) {
    if (showErrorModal) {
      showErrorModal(
        "현재 위치 확인 불가",
        "현재 위치를 확인할 수 없습니다. 위치 권한이 허용되었는지 확인해주세요."
      );
    }
  }
};

// 위치 권한 거부 시 안내
watch(
  () => isLocationPermissionDenied?.value,
  (isDenied) => {
    if (isDenied && showErrorModal) {
      showErrorModal(
        "현재 위치 확인 불가",
        "현재 위치를 확인할 수 없습니다. 위치 권한이 허용되었는지 확인해주세요."
      );
    }
  }
);

// 지도 초기화 및 showCurrentLocation 변경 시 마커 표시
watch(
  () => [props.showCurrentLocation, map],
  ([show, mapInstance]) => {
    if (show && mapInstance && currentLat.value && currentLng.value) {
      createCurrentLocationMarker(
        mapInstance,
        currentLat.value,
        currentLng.value
      );
    } else if (mapInstance) {
      removeCurrentLocationMarker();
    }
  }
);

const __returned__ = { props, emit, showToastMessage, serviceStore, shouldShowTurnPath, title, emitOnClickClose, emitOnClickDetailBtn, emitOnClickFavoriteBtn, refreshButton, isRotating, emitOnClickRefreshBtn, mapContainer, get map() { return map }, set map(v) { map = v }, addScript, getCenterOfPaths, drawDetourPaths, initMap, MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL, zoomLevel, increaseZoomLevel, decreaseZoomLevel, addZoomChangedNaverEventListener, removeZoomChangedNaverEventListener, createBusStopMarkers, getBusStopIconContents, updateSelectedBusStopMarkerWithInfoWindow, selectedBusStopId, handleBusStopClick, createBusLocationMarkers, removeBusLocationMarkers, drawNaverMapPaths, handleShowToastMessage, shouldShowRoadEventListModal, toggleShowRoadEventListModal, isSameBusLocation, getCurrentLocation, currentLocation, isLocationPermissionDenied, showErrorModal, currentLat, currentLng, createMarker, createCurrentLocationMarker, removeCurrentLocationMarker, moveMapToCurrentLocation, NavigationBar, MapZoomLevelSetter, get InlineSvg() { return InlineSvg }, LineInfoDetailHeader, RoadEventListModal, PageModal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})